import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Alert from "@mui/material/Alert";
import Button, { buttonClasses } from "@mui/material/Button";
import DialogTitle from "@mui/material/DialogTitle";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { Field, Form, Formik } from "formik";
import { useMemo, type FC } from "react";
import toast from "react-hot-toast";
import { PulseLoader } from "react-spinners";
import { TextAreaField } from "src/components/UI/Form";
import { vcAccountAssignments } from "src/queries/vc-account-assignments";
import { vendors } from "src/queries/vendors";
import { postVcAccountAssignments } from "../vc-account-assignments-api";
import type { StoreAccountAssignment, StoreAndPartnerSelection } from "../vc-account-assignments-types";
import { AssignmentsTable } from "./AssignmentsTable";

export interface AssignmentConfirmationFormProps {
  storeAndPartnerSelection: StoreAndPartnerSelection;
  clearStoreAndPartnerSelection(): void;
  setConfirmedAssignments(assignments: StoreAccountAssignment[]): void;
}

const formatErrorMessage = (str: string): string => str.split(":").slice(1).join(":").trim();

export const AssignmentConfirmationForm: FC<AssignmentConfirmationFormProps> = ({
  storeAndPartnerSelection,
  clearStoreAndPartnerSelection,
  setConfirmedAssignments,
}) => {
  const unassignedAccountsQuery = useQuery({
    ...vcAccountAssignments.unassigned(storeAndPartnerSelection.storeNames.length),
    staleTime: 1000,
    gcTime: 0,
  });

  const unassignedAccounts = unassignedAccountsQuery.data;
  const { vendorName, storeNames, partnerId } = storeAndPartnerSelection;

  const storeAccountAssignments: StoreAccountAssignment[] | null = useMemo(() => {
    if (!unassignedAccounts) return null;
    if (unassignedAccounts.length !== storeNames.length * 2) {
      console.error(`expected ${storeNames.length * 2} unassigned accounts, got ${unassignedAccounts.length}`);
      return null;
    }

    const result: StoreAccountAssignment[] = [];

    for (let i = 0; i < storeNames.length; i += 1) {
      result.push({
        storeName: storeNames[i],
        accounts: [unassignedAccounts[i * 2], unassignedAccounts[i * 2 + 1]],
      });
    }

    return result;
  }, [storeNames, unassignedAccounts]);

  const { data: partnerOptions } = useQuery(vendors.referralPartners);
  const selectedPartnerName = useMemo(() => {
    if (!partnerId) return "No partner";
    return partnerOptions?.find(partner => partner.id.toString() === partnerId)?.name || "Unknown partner";
  }, [partnerId, partnerOptions]);

  const queryClient = useQueryClient();
  const assignmentMutation = useMutation({
    mutationKey: ["vc-user-assignments", "create"],
    mutationFn: postVcAccountAssignments,
    onSuccess() {
      setConfirmedAssignments(storeAccountAssignments!);
      queryClient.invalidateQueries({ queryKey: vcAccountAssignments.assigned.queryKey });
    },
    onError() {
      toast("Fetching a new assortment of Vendor Central accounts...", {
        style: { textAlign: "center" },
        duration: 7500,
      });
      unassignedAccountsQuery.refetch();
    },
  });

  const canContinue =
    unassignedAccountsQuery.isSuccess &&
    !unassignedAccountsQuery.isFetching &&
    storeAccountAssignments &&
    !assignmentMutation.isPending;

  return (
    <>
      <DialogTitle style={{ border: "none" }}>Confirm account assignments</DialogTitle>
      <Formik
        initialValues={{ notes: "" }}
        onSubmit={({ notes }) =>
          assignmentMutation.mutate({ vendorName, assignments: storeAccountAssignments!, partnerId, notes })
        }
      >
        <Form>
          <div style={{ paddingInline: "1.5rem", paddingTop: 0, paddingBottom: "1rem" }}>
            <div style={{ marginBottom: "1rem" }}>
              <div style={{ lineHeight: 1.2, marginBottom: "1rem" }}>
                {unassignedAccountsQuery.isPending ||
                (unassignedAccountsQuery.isError && unassignedAccountsQuery.isFetching) ? (
                  <div
                    style={{
                      display: "flex",
                      gap: "0.5rem",
                      alignItems: "center",
                      marginBottom: "0.25rem",
                    }}
                  >
                    <PulseLoader size="8px" color="#EF1442" />
                    Fetching assortment of Vendor Central accounts...
                  </div>
                ) : unassignedAccountsQuery.isError ? (
                  <div>
                    <Alert severity="warning" color="error" sx={{ marginBottom: "2rem" }}>
                      <p style={{ marginBottom: "1rem" }}>
                        {isAxiosError<{ message: string }>(unassignedAccountsQuery.error)
                          ? formatErrorMessage(unassignedAccountsQuery.error.response?.data?.message || "") ||
                            unassignedAccountsQuery.error.message
                          : unassignedAccountsQuery.error.message}
                      </p>
                      <Button
                        variant="outlined"
                        onClick={() => unassignedAccountsQuery.refetch()}
                        sx={{ paddingBlock: "4px" }}
                      >
                        Try again
                      </Button>
                    </Alert>
                  </div>
                ) : (
                  <>
                    <p style={{ marginTop: "-1rem", marginBottom: "1rem" }}>
                      Please review and confirm the below account assignments are suitable
                    </p>
                    <div style={{ height: "400px", width: "100%" }}>
                      <AssignmentsTable
                        assignments={storeAccountAssignments}
                        isLoading={unassignedAccountsQuery.isFetching}
                      />
                    </div>
                    <Alert severity="info" sx={{ marginTop: 2, fontWeight: 400 }}>
                      <div style={{ width: "100%" }}>
                        <span>
                          <b>
                            {partnerId ? <>Selected partner: {selectedPartnerName}.</> : <>No partner selected.</>}{" "}
                            Vendor name: {vendorName}.
                          </b>
                        </span>
                        <hr style={{ marginBlock: "0.5rem" }} />
                        You will be able to copy the table to your clipboard in the next step.
                      </div>
                    </Alert>

                    <hr />
                    <Field
                      name="notes"
                      component={TextAreaField}
                      label="Notes"
                      disabled={assignmentMutation.isPending}
                    />
                  </>
                )}
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "1rem" }}>
              <Button
                type="button"
                startIcon={<ArrowBackIcon />}
                variant="outlined"
                onClick={clearStoreAndPartnerSelection}
                disabled={assignmentMutation.isPending}
              >
                Edit store/partner selection
              </Button>
              <Button
                type="submit"
                variant={assignmentMutation.isPending ? "outlined" : "contained"}
                color="primary"
                disabled={!canContinue}
                startIcon={assignmentMutation.isPending ? <PulseLoader size="8px" color="#777" /> : undefined}
                sx={assignmentMutation.isPending ? { [`&.${buttonClasses.disabled}`]: { color: "#444" } } : undefined}
              >
                {assignmentMutation.isPending ? (
                  <>Confirming account assignments...</>
                ) : (
                  <>Confirm account assignments</>
                )}
              </Button>
            </div>
          </div>
        </Form>
      </Formik>
    </>
  );
};
